/**
 * Particle system for managing ambient particles, explosions, and shockwaves
 */

import { Particle, Explosion, Shockwave } from "../types";
import { ANIMATION_CONFIG, COLORS } from "../constants";

export class ParticleSystem {
  private particles: Particle[] = [];
  private explosions: Explosion[] = [];
  private shockwaves: Shockwave[] = [];
  private isMobile: boolean = false;
  private maxParticles: number = 100;

  constructor(isMobile: boolean = false) {
    this.isMobile = isMobile;
    // Limit particles on mobile
    this.maxParticles = isMobile ? 50 : 100;
  }

  /**
   * Spawns ambient particles at the bottom of the canvas
   */
  spawnAmbientParticles(width: number, height: number): void {
    // Reduce spawn chance on mobile and limit total particles
    const spawnChance = this.isMobile
      ? ANIMATION_CONFIG.PARTICLE_SPAWN_CHANCE * 0.5
      : ANIMATION_CONFIG.PARTICLE_SPAWN_CHANCE;

    if (
      this.particles.length < this.maxParticles &&
      Math.random() < spawnChance
    ) {
      const spawnX = Math.random() * width;
      const sideColors = spawnX < width / 2 ? COLORS.left : COLORS.right;

      this.particles.push({
        x: spawnX,
        y: height + 10,
        vx: (Math.random() - 0.5) * 0.03, // ~0.5 pixels per frame at 60fps
        vy: (-Math.random() * 1.5 - 0.5) * 0.06, // ~1.5 pixels per frame at 60fps
        size: Math.random() * 2 + 1,
        opacity: 0,
        lifeMs: 0,
        maxLifeMs:
          ANIMATION_CONFIG.PARTICLE_MIN_LIFE_MS +
          Math.random() *
            (ANIMATION_CONFIG.PARTICLE_MAX_LIFE_MS -
              ANIMATION_CONFIG.PARTICLE_MIN_LIFE_MS),
        color: Math.random() > 0.5 ? sideColors.primary : sideColors.bright,
      });
    }
  }

  /**
   * Spawns particles at a specific position (for text fade effect)
   */
  spawnParticlesAtPosition(
    x: number,
    y: number,
    count: number,
    color: string
  ): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 20, // Small spread around position
        y: y + (Math.random() - 0.5) * 10,
        vx: (Math.random() - 0.5) * 0.09, // ~1.5 pixels per frame at 60fps
        vy: (-Math.random() * 2 - 1) * 0.06, // ~2 pixels per frame at 60fps
        size: Math.random() * 1.5 + 0.5, // Smaller particles
        opacity: 0,
        lifeMs: 0,
        maxLifeMs:
          ANIMATION_CONFIG.PARTICLE_TEXT_MIN_LIFE_MS +
          Math.random() *
            (ANIMATION_CONFIG.PARTICLE_TEXT_MAX_LIFE_MS -
              ANIMATION_CONFIG.PARTICLE_TEXT_MIN_LIFE_MS),
        color: color,
      });
    }
  }

  /**
   * Adds an explosion effect
   */
  addExplosion(x: number, y: number, size: number, color: string): void {
    this.explosions.push({
      x,
      y,
      radius: 0,
      maxRadius: size,
      opacity: 1,
      color,
    });
  }

  /**
   * Adds a shockwave effect
   */
  addShockwave(
    x: number,
    y: number,
    size: number,
    color: string,
    lineWidth: number = 2
  ): void {
    this.shockwaves.push({
      x,
      y,
      radius: 0,
      maxRadius: size,
      opacity: 1,
      color,
      lineWidth,
    });
  }

  /**
   * Updates and draws all particles
   */
  updateAndDrawParticles(ctx: CanvasRenderingContext2D, deltaMs: number): void {
    // Batch similar particles for better performance
    const particlesByColor: Map<string, Particle[]> = new Map();

    this.particles = this.particles.filter((particle) => {
      // Update physics based on time
      particle.x += particle.vx * deltaMs;
      particle.y += particle.vy * deltaMs;
      particle.lifeMs += deltaMs;

      // Update opacity based on time
      const fadeInMs = 333; // ~20 frames at 60fps
      const fadeOutMs = 333;

      if (particle.lifeMs < fadeInMs) {
        particle.opacity = particle.lifeMs / fadeInMs;
      } else if (particle.lifeMs > particle.maxLifeMs - fadeOutMs) {
        particle.opacity = (particle.maxLifeMs - particle.lifeMs) / fadeOutMs;
      } else {
        particle.opacity = 1;
      }

      // Remove if dead
      if (particle.lifeMs >= particle.maxLifeMs || particle.y < -10) {
        return false;
      }

      // Group by color for batch rendering
      const key = `${particle.color}_${Math.round(particle.opacity * 10)}`;
      if (!particlesByColor.has(key)) {
        particlesByColor.set(key, []);
      }
      particlesByColor.get(key)!.push(particle);

      return true;
    });

    // Batch render particles by color/opacity
    particlesByColor.forEach((particles, key) => {
      const [color, opacityStr] = key.split("_");
      const opacity = parseInt(opacityStr) / 10;

      ctx.save();
      ctx.globalAlpha = opacity * 0.6;
      ctx.fillStyle = color;

      // Draw all particles of the same color/opacity in one batch
      particles.forEach((particle) => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();
    });
  }

  /**
   * Updates and draws all explosions
   */
  updateAndDrawExplosions(
    ctx: CanvasRenderingContext2D,
    deltaMs: number
  ): void {
    // Skip complex gradients on mobile
    if (this.isMobile) {
      this.explosions = this.explosions.filter((explosion) => {
        // Expand and fade based on time
        const expansionRate =
          1 -
          Math.pow(
            1 - ANIMATION_CONFIG.EXPLOSION_EXPANSION_RATE,
            deltaMs / 16.67
          ); // Normalize to 60fps
        explosion.radius +=
          (explosion.maxRadius - explosion.radius) * expansionRate * 1.5;
        explosion.opacity *= Math.pow(
          ANIMATION_CONFIG.EXPLOSION_FADE_PER_MS,
          deltaMs
        );

        if (explosion.opacity < 0.01) return false;

        // Simplified explosion for mobile - just a colored circle
        ctx.save();
        ctx.globalAlpha = explosion.opacity * 0.7;
        ctx.fillStyle = explosion.color;
        ctx.beginPath();
        ctx.arc(explosion.x, explosion.y, explosion.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        return true;
      });
    } else {
      this.explosions = this.explosions.filter((explosion) => {
        // Expand and fade based on time
        const expansionRate =
          1 -
          Math.pow(
            1 - ANIMATION_CONFIG.EXPLOSION_EXPANSION_RATE,
            deltaMs / 16.67
          );
        explosion.radius +=
          (explosion.maxRadius - explosion.radius) * expansionRate;
        explosion.opacity *= Math.pow(
          ANIMATION_CONFIG.EXPLOSION_FADE_PER_MS,
          deltaMs
        );

        if (explosion.opacity < 0.01) return false;

        // Draw explosion with gradient
        ctx.save();
        ctx.globalAlpha = explosion.opacity;
        ctx.globalCompositeOperation = "lighter";

        const gradient = ctx.createRadialGradient(
          explosion.x,
          explosion.y,
          0,
          explosion.x,
          explosion.y,
          explosion.radius
        );
        gradient.addColorStop(0, explosion.color);
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(explosion.x, explosion.y, explosion.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        return true;
      });
    }
  }

  /**
   * Updates and draws all shockwaves
   */
  updateAndDrawShockwaves(
    ctx: CanvasRenderingContext2D,
    deltaMs: number
  ): void {
    this.shockwaves = this.shockwaves.filter((shockwave) => {
      // Expand and fade based on time
      const expansionRate =
        1 -
        Math.pow(
          1 - ANIMATION_CONFIG.SHOCKWAVE_EXPANSION_RATE,
          deltaMs / 16.67
        );
      shockwave.radius +=
        (shockwave.maxRadius - shockwave.radius) * expansionRate;
      shockwave.opacity *= Math.pow(
        ANIMATION_CONFIG.SHOCKWAVE_FADE_PER_MS,
        deltaMs
      );

      if (shockwave.opacity < 0.01) return false;

      // Draw shockwave
      ctx.save();
      ctx.globalAlpha = shockwave.opacity;
      ctx.strokeStyle = shockwave.color;
      ctx.lineWidth =
        shockwave.lineWidth * (1 - shockwave.radius / shockwave.maxRadius);

      ctx.beginPath();
      ctx.arc(shockwave.x, shockwave.y, shockwave.radius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();

      return true;
    });
  }

  /**
   * Clears all particles and effects
   */
  clear(): void {
    this.particles = [];
    this.explosions = [];
    this.shockwaves = [];
  }
}
